import * as log from "@/actions/logger";
// We keep server-only imports inside functions to avoid bundling issues in client builds

export type dictionary = Record<string, string>;

const dictionaries = new Map<string, () => Promise<dictionary>>([
  ["en-US", () => import("./en.json").then((module) => module.default)],
  ["es", () => import("./es.json").then((module) => module.default)],
]);

export interface translationFunction {
  (s: string, params?: Params): string;
}

export type Params = Record<string, string>;

const getDictionary = async (locale: string) =>
  dictionaries.get(locale)?.() ?? dictionaries.get("en-US")?.();

/**
 * Translates a key using the provided dictionary with optional parameter replacement
 *
 * @param s - The translation key to look up
 * @param dictionary - The dictionary object containing translation key-value pairs
 * @param params - Optional parameters to replace placeholders in the translation string
 * @returns The translated string with parameters replaced, or the key itself if not found
 *
 * @example
 * const dict = { "welcome": "Welcome {{name}}" };
 * translateKey("welcome", dict, { name: "John" }); // Returns: "Welcome John"
 *
 * @example
 * const dict = { "hello": "Hello" };
 * translateKey("hello", dict); // Returns: "Hello"
 * translateKey("missing", dict); // Returns: "missing" (fallback to key)
 */
export const translateKey = (s: string, dictionary: dictionary | undefined, params?: Params) => {
  let result = s;
  if (!dictionary || !(s in dictionary)) {
    return result;
  }
  result = dictionary[s];

  if (params) {
    // Replace placeholders in the translation string (e.g., {{domain}})
    Object.keys(params).forEach((key) => {
      result = result.replace(`{{${key}}}`, params[key]);
    });
  }

  return result;
};

export const getTranslation = async (
  locale: string,
  fileName?: string
): Promise<translationFunction> => {
  let dictionary: dictionary | undefined;
  try {
    if (fileName) {
      // Load from file structure: ./${locale}/${fileName}.json
      const rawDictionary = await import(`./${locale}/${fileName}.json`).then(
        (module) => module.default
      );
      dictionary = flattenDictionary(rawDictionary);
    } else {
      // Load from hardcoded dictionaries (en.json, es.json)
      dictionary = await getDictionary(locale);
    }
  } catch (error) {
    log.warn(
      { module: "i18n" },
      `Translation for ${fileName ? `file "${fileName}" and ` : ""}language "${locale}" not found. Falling back to default.`
    );
  } finally {
    return (s: string, params?: Params) => translateKey(s, dictionary, params);
  }
};

/**
 * Recursively flattens a nested object into a single-level dictionary with dot-notation keys
 *
 * Converts nested objects into a flat structure where keys are joined with dots.
 * This allows accessing nested translations using dot notation (e.g., "footer.contact.title")
 *
 * @param source - The object to flatten (typically a JSON translation file)
 * @param parentKey - Internal parameter used during recursion to track the key path
 * @returns A flattened dictionary with dot-notation keys
 *
 * @example
 * const nested = {
 *   footer: {
 *     contact: "Contact Us",
 *     copyright: "© 2024"
 *   },
 *   header: {
 *     menu: {
 *       home: "Home"
 *     }
 *   }
 * };
 * flattenDictionary(nested);
 * // Returns:
 * // {
 * //   "footer.contact": "Contact Us",
 * //   "footer.copyright": "© 2024",
 * //   "header.menu.home": "Home"
 * // }
 *
 * @example
 * const simple = { "hello": "Hello World" };
 * flattenDictionary(simple);
 * // Returns: { "hello": "Hello World" }
 */
export const flattenDictionary = (source: unknown, parentKey = ""): dictionary => {
  if (typeof source !== "object" || source === null) {
    return {};
  }

  const output: dictionary = {};
  for (const [key, value] of Object.entries(source as Record<string, unknown>)) {
    const currentKey = parentKey ? `${parentKey}.${key}` : key;

    if (typeof value === "string") {
      output[currentKey] = value;
      continue;
    }

    if (typeof value === "object" && value !== null) {
      Object.assign(output, flattenDictionary(value, currentKey));
      continue;
    }

    if (value !== undefined) {
      output[currentKey] = String(value);
    }
  }

  return output;
};
